import { useEffect, useState } from "react";
import { Link, useLocation } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X } from "lucide-react";
import { Logo } from "./Logo";
import { MagneticButton } from "./MagneticButton";
import { WA_LINKS } from "../lib/utils";
import { cn } from "../lib/utils";

const NAV_LINKS = [
  { href: "/", label: "Home" },
  { href: "/services", label: "Services" },
  { href: "/about", label: "About" },
  { href: "/contact", label: "Contact" },
];

export function Navbar() {
  const location = useLocation();
  const pathname = location.pathname;
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => { setMobileOpen(false); }, [pathname]);

  return (
    <>
      <motion.header
        initial={{ y: -40, opacity: 0 }}
        animate={{ y: 0, opacity: 1 }}
        transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
        className={cn("fixed inset-x-0 top-0 z-50 transition-all duration-500", scrolled ? "bg-white/80 backdrop-blur-xl border-b border-navy-100/60" : "bg-transparent")}
      >
        <div className="container-luxury">
          <nav className="flex h-20 items-center justify-between">
            <Link to="/" aria-label="AssetCare365 home"><Logo /></Link>

            <ul className="hidden items-center gap-10 lg:flex">
              {NAV_LINKS.map((link) => {
                const isActive = link.href === "/" ? pathname === "/" : pathname.startsWith(link.href);
                return (
                  <li key={link.href}>
                    <Link to={link.href} className={cn("relative text-sm tracking-tight transition-colors", isActive ? "text-navy-800" : "text-navy-700/70 hover:text-navy-800")} data-testid={`nav-${link.label.toLowerCase()}`}>
                      {link.label}
                      {isActive && <motion.span layoutId="nav-active" className="absolute -bottom-1.5 left-0 right-0 mx-auto h-px w-5 bg-emerald-400" />}
                    </Link>
                  </li>
                );
              })}
            </ul>

            <div className="hidden lg:block">
              <MagneticButton href={WA_LINKS.call} external size="md" variant="primary" data-testid="nav-cta">Book a 15-min Call</MagneticButton>
            </div>

            <button type="button" aria-label="Toggle menu" className="lg:hidden p-2 -mr-2 text-navy-800" onClick={() => setMobileOpen((v) => !v)} data-testid="mobile-menu-btn">
              {mobileOpen ? <X size={22} /> : <Menu size={22} />}
            </button>
          </nav>
        </div>
      </motion.header>

      <AnimatePresence>
        {mobileOpen && (
          <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} transition={{ duration: 0.3 }} className="fixed inset-0 z-40 bg-white lg:hidden pt-20">
            <div className="container-luxury pt-8">
              <ul className="flex flex-col gap-2">
                {NAV_LINKS.map((link, i) => (
                  <motion.li key={link.href} initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.05 + i * 0.05, duration: 0.5 }}>
                    <Link to={link.href} className="block border-b border-navy-100 py-5 font-display text-3xl text-navy-800">{link.label}</Link>
                  </motion.li>
                ))}
              </ul>
              <motion.div initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3, duration: 0.5 }} className="mt-10">
                <MagneticButton href={WA_LINKS.call} external size="lg" variant="primary" className="w-full">Book a 15-min Call</MagneticButton>
              </motion.div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
